import { useMemo, useState } from "react";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useBayHireRequests } from "@features/bay-hire/hooks/useBayHireQueries";
import {
  bayHirePaymentTypeLabel,
  bayHirePreferenceLabel,
  bayHireStatusLabel,
  toBayHireSelectOptions,
} from "@features/bay-hire/utils/bayHireLabels";
import {
  applyDateRangeContainmentFilter,
  formatBayHireDateTime,
} from "@features/bay-hire/utils/formatBayHireDate";
// DFT-0027 (Slice E): shared with the Operations queues (see statusBadge.ts).
import { getBayHireStatusBadgeType } from "@features/bay-hire/utils/statusBadge";
import type { BayHireSearchField, BayHireSearchFilter } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

// DFT-0026 (item 5): page/pageSize are localStorage-persisted, mirroring
// useBayHireOperationsQueue's pattern (DFT-0015) and every comparator
// container (Gatepass/Security Clearance/Licensing/Vessel Call/Vessel
// Management) - this list previously used plain useState, the one
// remaining pagination-persistence divergence identified during DFT-0026.
const PAGE_KEY = "bay-hire-request-list-page";
const PAGE_SIZE_KEY = "bay-hire-request-list-page-size";
const FILTERS_KEY = "bay-hire-request-list-filters";
// DFT-0033: page 11's design defaults to 10 rows per page (was 25).
const DEFAULT_PAGE_SIZE = 10;

// DFT-0033/DFT-0069: translates the Filters panel's generic FilterItem[] directly into the
// generic BayHireSearchFilter[] the backend now expects - mirrors
// vessel-call-management-service/PortCalls.tsx's own buildFilterPayload exactly (no
// intermediate named-field DTO). FilterItems sharing the same `field` are merged into one
// entry's `values` array (OR semantics) rather than overwriting each other.
const pushFilter = (filters: BayHireSearchFilter[], field: BayHireSearchField, value: string) => {
  const existing = filters.find((f) => f.field === field);
  if (existing) {
    if (!existing.values.includes(value)) existing.values.push(value);
  } else {
    filters.push({ field, values: [value] });
  }
};

const buildFiltersFromUI = (items: FilterItem[]): BayHireSearchFilter[] => {
  const filters: BayHireSearchFilter[] = [];
  for (const item of items) {
    if (!item.field || item.value === "" || item.value == null) continue;
    switch (item.field) {
      case "status":
        pushFilter(filters, "status", item.value as string);
        break;
      case "paymentType":
        pushFilter(filters, "paymentType", item.value as string);
        break;
      case "bayPreference":
        pushFilter(filters, "bayPreference", item.value as string);
        break;
      case "dateSubmitted": {
        const range = item.value as { from?: string; to?: string };
        if (range.from) pushFilter(filters, "createdAtFrom", range.from);
        if (range.to) pushFilter(filters, "createdAtTo", range.to);
        break;
      }
      // DFT-0063: "ID" filters on requestNo (the human-facing reference number, e.g. "123"),
      // not the UUID `id`. Numeric-only text field (minLength 1), same shape as numberOfBays
      // below.
      case "requestNo": {
        const value = item.value as string;
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) pushFilter(filters, "requestNo", String(parsed));
        break;
      }
      case "responsiblePersonName":
        pushFilter(filters, "responsiblePersonName", item.value as string);
        break;
      // DFT-0075: "Date" (dateRange) is a containment filter - only requests whose entire hire
      // period falls inside [dateFrom, dateTo] match (request.dateFrom >= filter's "from" AND
      // request.dateTo <= filter's "to"; either bound independently optional). Stays date-only
      // in the UI (FiltersPanel's "dateFromToWithoutTime" type, unchanged - no time picker) but
      // the backend expects a fully offset-aware timestamp,
      // matching the platform convention confirmed against gatepass-service (GatepassSpecifications
      // takes a pre-built OffsetDateTime, never a bare date) and vessel-call-management-service's
      // Movements module (no date-only field exists there at all) - neither does backend-side
      // date-only parsing. Rather than change FiltersPanel.tsx's shared formatDateOnly/
      // formatDateOnlyEndOfDay (used by port-security/licensing/gatepass/vessel-call too), the
      // UTC-offset conversion happens here, scoped to Bay Hire's own dispatch: range.from is
      // "yyyy-MM-dd" (LOCAL calendar day - a bare date-only string alone would parse as UTC per
      // the ES spec, so "T00:00:00" is appended before conversion to force local-time
      // interpretation), range.to is already a local date-TIME string with no offset
      // ("yyyy-MM-ddT23:59:59.999"). localDateBoundaryToUtcIso does the same real local-to-UTC
      // conversion useBayHireRequestForm.tsx's toISOInstant does for the request's own
      // dateFrom/dateTo at creation time - see its own doc comment for the bug this fixes (found
      // live 2026-08-28: a naive string-concatenated "Z" let an adjacent local calendar day leak
      // into the filtered results for any non-zero UTC offset).
      case "dateRange":
        applyDateRangeContainmentFilter(
          filters,
          pushFilter,
          item.value as { from?: string; to?: string },
        );
        break;
      case "numberOfBays": {
        const value = item.value as string;
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) pushFilter(filters, "numberOfBays", String(parsed));
        break;
      }
      default:
        break;
    }
  }
  return filters;
};

export const useBayHireRequestList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(PAGE_KEY);
    return saved ? Number.parseInt(saved, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(PAGE_SIZE_KEY);
    return saved ? Number.parseInt(saved, 10) : DEFAULT_PAGE_SIZE;
  });
  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const requestFilters = useMemo(() => buildFiltersFromUI(filters), [filters]);

  // page is 1-based in the UI (matches CustomDataTable); the backend is 0-based.
  const { data, isLoading, isError } = useBayHireRequests(page - 1, pageSize, requestFilters);
  const totalRecordCount = data?.totalRecordCount ?? 0;

  // DFT-0063: ordered to match the grid's own column order (ID | Name of Port company
  // responsible | No of bays | Request Bay | Payment type | Date From/Date To | Date
  // submitted | Status) - see the headers array below.
  const availableFields = useMemo(
    () => ({
      requestNo: {
        label: t("bay-hire.table.id"),
        type: "integer" as const,
        minLength: 1,
      },
      responsiblePersonName: {
        label: t("bay-hire.table.responsiblePerson"),
        type: "text" as const,
        minLength: 3,
      },
      numberOfBays: {
        label: t("bay-hire.table.bayNo"),
        type: "integer" as const,
        minLength: 1,
      },
      bayPreference: {
        label: t("bay-hire.table.requestBay"),
        type: "select" as const,
        options: toBayHireSelectOptions(["INSIDE_PORT", "OUTSIDE_PORT"], bayHirePreferenceLabel, t),
      },
      paymentType: {
        label: t("bay-hire.table.paymentType"),
        type: "select" as const,
        options: toBayHireSelectOptions(
          ["MONTHLY", "YEARLY", "EXEMPTED"],
          bayHirePaymentTypeLabel,
          t,
        ),
      },
      // DFT-0075: "Date" is a containment filter - see buildFiltersFromUI's dateRange case for
      // the rationale.
      dateRange: {
        label: t("bay-hire.filters.dateRange"),
        type: "dateFromToWithoutTime" as const,
      },
      // DFT-0075: full offset-aware date-time, matching vessel-call-management-service's
      // Movements module convention (createdAt is a real timestamp, not a calendar date) -
      // "dateFromToWithoutTime" sent an offset-less string that Instant.parse() on the backend
      // rejected with an unhandled 500. FiltersPanel.tsx's isDateFromToWithTime check
      // (config.type === "dateFromTo") already sends date.toISOString() for this type, so no
      // backend change is needed - see working/07-technical-approach.md.
      dateSubmitted: {
        label: t("bay-hire.table.dateSubmitted"),
        type: "dateFromTo" as const,
      },
      status: {
        label: t("bay-hire.table.status"),
        type: "select" as const,
        // DFT-0044: humanized options, matching the detail page/status column (FE-19/36) -
        // previously showed the raw enum ("REQUESTED") in the Filters dropdown.
        options: toBayHireSelectOptions(
          [
            "REQUESTED",
            "PRE_APPROVED",
            "PAYED",
            "APPROVED",
            "REJECTED",
            "CANCELLED",
            "WAITING_LIST",
          ],
          bayHireStatusLabel,
          t,
        ),
      },
    }),
    [t],
  );

  const headers: Header[] = useMemo(
    () => [
      // FE-17: `id` is a UUID, kept only for row navigation (CustomDataTable's canPreview reads
      // `row.id` directly, unconditionally - unaffected by this header's own `field`). The visible
      // "ID" column reads `requestNo` instead, the human reference number ("123"), matching the
      // detail page's own heading and breadcrumb - the SPA's `BayHireRequest` type never carried
      // this field at all before, despite the backend returning it on every read.
      // Megaplan (2026-08-05): every header below now carries an explicit, content-proportional
      // `flex` - previously all 9 columns fell through to columns.tsx's shared default
      // (flex: 1, minWidth: 130), rendering equal-width regardless of content. Identical flex
      // values are applied to this shape's two other copies (BayHireOperationsQueue,
      // BayHireWaitingListQueue) since all three lists share it.
      { field: "requestNo", columnName: t("bay-hire.table.id"), flex: 0.6 },
      // FE-18: the baseline (04_Port_License_details-2.png) orders the company responsible 2nd and
      // the bay count 3rd. Batch 2 leaves this order unchanged. The wireframes labeled the count
      // column inconsistently between screens (`Bay No` on two grids, `No of bays` on a third) -
      // Megaplan (2026-08-05) resolves that in favor of `No of bays` (see the header below).
      {
        field: "responsiblePersonName",
        columnName: t("bay-hire.table.responsiblePerson"),
        flex: 1.6,
      },
      {
        field: "numberOfBays",
        // Megaplan (2026-08-05): "Bay No" mislabeled the bay COUNT here, conflicting with the
        // Bays table's own "Bay No" column (the bay CODE) - renamed to "No of bays" to resolve
        // the inconsistency this file's own comment used to flag (the wireframes disagreed with
        // themselves on this label across screens).
        columnName: t("bay-hire.table.bayNo"),
        flex: 0.6,
      },
      {
        // DFT-0044: reads the humanized `bayPreferenceLabel` the row mapper adds below, NOT the
        // raw `bayPreference` field itself - that stays untouched since it also seeds the Update
        // Request form's initial value and nothing here should silently break that.
        field: "bayPreferenceLabel",
        columnName: t("bay-hire.table.requestBay"),
        flex: 0.9,
      },
      {
        field: "paymentTypeLabel",
        columnName: t("bay-hire.table.paymentType"),
        flex: 0.8,
      },
      {
        field: "dateFrom",
        columnName: t("bay-hire.table.dateFrom"),
        type: "date",
        flex: 0.8,
      },
      {
        field: "dateTo",
        columnName: t("bay-hire.table.dateTo"),
        type: "date",
        flex: 0.8,
      },
      {
        // DFT-0044 (FE-29): NOT type: "dateTime" - see formatBayHireDate.ts's
        // formatBayHireDateTime for why this deliberately falls through to columns.tsx's plain
        // default column instead, matching every other Marhub feature's own date+time column.
        field: "createdAt",
        columnName: t("bay-hire.table.dateSubmitted"),
        flex: 1.0,
      },
      {
        // DFT-0033: field literally "status" (not renderAsChip) so
        // columns.tsx renders the colored-dot Badge, using this row's
        // explicit badgeType (set below from getBayHireStatusBadgeType)
        // rather than columns.tsx's own generic getBadgeType, which only
        // recognizes human-readable statuses ("Approved") - Bay Hire's raw
        // domain values ("APPROVED") wouldn't otherwise match any case.
        field: "status",
        columnName: t("bay-hire.table.status"),
        flex: 0.9,
      },
    ],
    [t],
  );

  const rows = useMemo(
    () =>
      (data?.records ?? []).map((request) => ({
        ...request,
        numberOfBays: request.numberOfBays ?? "-",
        // Defensive fallback, matching numberOfBays' own convention above - the backend always
        // returns requestNo (BIGSERIAL NOT NULL) so this should not be reachable in practice, but
        // an empty cell reads better than `undefined` if it ever were.
        requestNo: request.requestNo ?? "-",
        badgeType: getBayHireStatusBadgeType(request.status),
        // DFT-0044 (FE-19): humanized display text for columns.tsx's status column
        // (columns.tsx reads `statusLabel` when present, falling back to the raw `status` this
        // row still carries unchanged - action guards elsewhere key off that raw value).
        statusLabel: t(`bay-hire.status.${request.status}`, { defaultValue: request.status }),
        // DFT-0044 (FE-29): DD/MM/YYYY HH:MM, matching every other Marhub feature's own
        // date+time column (see formatBayHireDate.ts's formatBayHireDateTime) - overwrites the
        // raw ISO instant, which nothing else in this list reads.
        createdAt: formatBayHireDateTime(request.createdAt),
        // DFT-0044: humanized display text for the Request Bay/Payment type columns - `bayPreference`
        // and `paymentType` themselves stay raw (Update Request's initial form values read them).
        bayPreferenceLabel: bayHirePreferenceLabel(t, request.bayPreference),
        paymentTypeLabel: bayHirePaymentTypeLabel(t, request.paymentType),
      })),
    [data, t],
  );

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
    localStorage.setItem(PAGE_KEY, String(newPage));
    localStorage.setItem(PAGE_SIZE_KEY, String(newPageSize));
  };

  const handleApplyFilters = (newFilters: FilterItem[]) => {
    setFilters(newFilters);
    localStorage.setItem(FILTERS_KEY, JSON.stringify(newFilters));
    setPage(1);
    localStorage.setItem(PAGE_KEY, "1");
  };

  const handleClearFilters = () => {
    setFilters([]);
    localStorage.removeItem(FILTERS_KEY);
    setPage(1);
    localStorage.setItem(PAGE_KEY, "1");
  };

  return {
    headers,
    rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalRecordCount,
    handlePageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  };
};
